import React, { useEffect, useState } from 'react';
import { blogsApi } from '../api/client';
import type { Blog, BlogPost, BlogStats } from '../api/client';
import './Blogs.css';

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [stats, setStats] = useState<BlogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateBlogModal, setShowCreateBlogModal] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'blogs' | 'posts'>('overview');

  const [newBlog, setNewBlog] = useState({
    title: '',
    description: '',
    theme: 'default',
    status: 'active' as 'active' | 'inactive',
    isPublic: true,
    allowComments: true,
    category: 'general',
    tags: [] as string[]
  });

  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    status: 'draft' as 'draft' | 'published',
    tags: [] as string[],
    blogId: '',
    allowComments: true,
    isFeatured: false
  });

  useEffect(() => {
    fetchBlogData();
  }, []);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      const [blogsRes, postsRes, statsRes] = await Promise.all([
        blogsApi.getBlogs().catch(() => ({ data: [] })),
        blogsApi.getBlogPosts().catch(() => ({ data: [] })),
        blogsApi.getBlogStats().catch(() => ({ data: null }))
      ]);

      setBlogs(blogsRes.data);
      setBlogPosts(postsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      setError('Failed to load blog data');
      console.error('Blog data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlog = async () => {
    if (!newBlog.title.trim()) return;

    try {
      const blogData = {
        userId: '550e8400-e29b-41d4-a716-446655440000', // Demo user ID (brian)
        title: newBlog.title,
        description: newBlog.description,
        theme: newBlog.theme,
        status: newBlog.status,
        settings: {
          isPublic: newBlog.isPublic,
          allowComments: newBlog.allowComments,
          moderateComments: false,
          showAuthor: true,
          showDate: true,
          postsPerPage: 10
        },
        metadata: {
          category: newBlog.category,
          tags: newBlog.tags,
          language: 'en'
        }
      };

      await blogsApi.createBlog(blogData);
      
      setNewBlog({
        title: '',
        description: '',
        theme: 'default',
        status: 'active',
        isPublic: true,
        allowComments: true,
        category: 'general',
        tags: []
      });
      
      setShowCreateBlogModal(false);
      fetchBlogData();
    } catch (err) {
      console.error('Error creating blog:', err);
      setError('Failed to create blog');
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.blogId) return;

    try {
      const postData = {
        userId: '550e8400-e29b-41d4-a716-446655440000', // Demo user ID (brian)
        blogId: newPost.blogId,
        title: newPost.title,
        content: newPost.content,
        status: newPost.status,
        tags: newPost.tags,
        settings: {
          allowComments: newPost.allowComments,
          isFeatured: newPost.isFeatured,
          isPinned: false
        }
      };

      await blogsApi.createBlogPost(postData);
      
      setNewPost({
        title: '',
        content: '',
        status: 'draft',
        tags: [],
        blogId: '',
        allowComments: true,
        isFeatured: false
      });
      
      setShowCreatePostModal(false);
      fetchBlogData();
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create blog post');
    }
  };

  const handleDeleteBlog = async (blogId: string) => {
    if (!window.confirm('Are you sure you want to delete this blog? This will also delete all posts.')) return;

    try {
      await blogsApi.deleteBlog(blogId);
      fetchBlogData();
    } catch (err) {
      console.error('Error deleting blog:', err);
      setError('Failed to delete blog');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await blogsApi.deleteBlogPost(postId);
      fetchBlogData();
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
    }
  };

  if (loading) {
    return (
      <div className="blogs-page">
        <div className="loading">Loading blog data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blogs-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="blogs-page">
      <header className="blogs-header">
        <h1>Blog Management</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateBlogModal(true)}
          >
            Create Blog
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowCreatePostModal(true)}
            disabled={blogs.length === 0}
          >
            Create Post
          </button>
        </div>
      </header>

      <div className="blogs-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'blogs' ? 'active' : ''}`}
          onClick={() => setActiveTab('blogs')}
        >
          Blogs ({blogs.length})
        </button>
        <button 
          className={`tab ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          Posts ({blogPosts.length})
        </button>
      </div>

      <div className="blogs-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Total Blogs</h3>
                  <p className="stat-number">{stats.totalBlogs}</p>
                </div>
                <div className="stat-card">
                  <h3>Active Blogs</h3>
                  <p className="stat-number">{stats.activeBlogs}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Posts</h3>
                  <p className="stat-number">{stats.totalPosts}</p>
                </div>
                <div className="stat-card">
                  <h3>Published Posts</h3>
                  <p className="stat-number">{stats.publishedPosts}</p>
                </div>
                <div className="stat-card">
                  <h3>Draft Posts</h3>
                  <p className="stat-number">{stats.draftPosts}</p>
                </div>
                <div className="stat-card">
                  <h3>Featured Posts</h3>
                  <p className="stat-number">{stats.featuredPosts}</p>
                </div>
              </div>
            )}

            <div className="recent-activity">
              <h3>Recent Blog Posts</h3>
              <div className="posts-list">
                {blogPosts.slice(0, 5).map(post => (
                  <div key={post.id} className="post-item">
                    <div className="post-info">
                      <h4>{post.title}</h4>
                      <p className="post-meta">
                        {blogs.find(b => b.id === post.blogId)?.title} • 
                        {post.status} • 
                        {new Date(post.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="post-actions">
                      <button 
                        className="btn btn-small"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'blogs' && (
          <div className="blogs-tab">
            <div className="blogs-grid">
              {blogs.map(blog => (
                <div key={blog.id} className="blog-card">
                  <div className="blog-header">
                    <h3>{blog.title}</h3>
                    <span className={`status ${blog.status}`}>{blog.status}</span>
                  </div>
                  <p className="blog-description">{blog.description}</p>
                  <div className="blog-meta">
                    <span>Theme: {blog.theme}</span>
                    <span>Posts: {blogPosts.filter(p => p.blogId === blog.id).length}</span>
                    <span>{blog.settings.isPublic ? 'Public' : 'Private'}</span>
                  </div>
                  <div className="blog-actions">
                    <button 
                      className="btn btn-small"
                      onClick={() => setActiveTab('posts')}
                    >
                      View Posts
                    </button>
                    <button 
                      className="btn btn-small btn-danger"
                      onClick={() => handleDeleteBlog(blog.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'posts' && (
          <div className="posts-tab">
            <div className="posts-grid">
              {blogPosts.map(post => {
                const blog = blogs.find(b => b.id === post.blogId);
                return (
                  <div key={post.id} className="post-card">
                    <div className="post-header">
                      <h3>{post.title}</h3>
                      <span className={`status ${post.status}`}>{post.status}</span>
                    </div>
                    <p className="post-excerpt">{post.excerpt}</p>
                    <div className="post-meta">
                      <span>Blog: {blog?.title}</span>
                      <span>Reading time: {post.metadata.readingTime}min</span>
                      <span>Words: {post.metadata.wordCount}</span>
                    </div>
                    <div className="post-tags">
                      {post.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                    <div className="post-actions">
                      <button 
                        className="btn btn-small btn-danger"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Create Blog Modal */}
      {showCreateBlogModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New Blog</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateBlogModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newBlog.title}
                  onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })}
                  placeholder="Blog title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newBlog.description}
                  onChange={(e) => setNewBlog({ ...newBlog, description: e.target.value })}
                  placeholder="Blog description"
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Theme</label>
                  <select
                    value={newBlog.theme}
                    onChange={(e) => setNewBlog({ ...newBlog, theme: e.target.value })}
                  >
                    <option value="default">Default</option>
                    <option value="minimal">Minimal</option>
                    <option value="dark">Dark</option>
                    <option value="modern">Modern</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newBlog.status}
                    onChange={(e) => setNewBlog({ ...newBlog, status: e.target.value as 'active' | 'inactive' })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={newBlog.category}
                  onChange={(e) => setNewBlog({ ...newBlog, category: e.target.value })}
                  placeholder="Blog category"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newBlog.isPublic}
                    onChange={(e) => setNewBlog({ ...newBlog, isPublic: e.target.checked })}
                  />
                  Public blog
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newBlog.allowComments}
                    onChange={(e) => setNewBlog({ ...newBlog, allowComments: e.target.checked })}
                  />
                  Allow comments
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreateBlogModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateBlog}
              >
                Create Blog
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showCreatePostModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New Post</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreatePostModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Blog *</label>
                <select
                  value={newPost.blogId}
                  onChange={(e) => setNewPost({ ...newPost, blogId: e.target.value })}
                >
                  <option value="">Select a blog</option>
                  {blogs.map(blog => (
                    <option key={blog.id} value={blog.id}>{blog.title}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="Post title"
                />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Post content (supports Markdown)"
                  rows={8}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={newPost.status}
                  onChange={(e) => setNewPost({ ...newPost, status: e.target.value as 'draft' | 'published' })}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPost.allowComments}
                    onChange={(e) => setNewPost({ ...newPost, allowComments: e.target.checked })}
                  />
                  Allow comments
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPost.isFeatured}
                    onChange={(e) => setNewPost({ ...newPost, isFeatured: e.target.checked })}
                  />
                  Featured post
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreatePostModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreatePost}
              >
                Create Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blogs;